import { isPlatformBrowser } from '@angular/common';
import { Injectable, InjectionToken, PLATFORM_ID, inject } from '@angular/core';

import { AccaBoostConfig } from '@frontend/sports/common/client-config-data-access';
import { OptionalNonRootToken } from '@frontend/sports/host-app/sports-product/feature/non-root-token';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { UserService } from '@frontend/sports/user/feature';
import { UserEvent, UserLoginEvent } from '@frontend/vanilla/core';
import { Observable, ReplaySubject, Subject, concat, map, merge, of, startWith, switchMap } from 'rxjs';

import { AccaBoostToken } from '../../betslip/modules/reward-tokens/reward-tokens.model';
import { AccaBoostTokenLoaderService } from './acca-boost-token-loader.service';

export const ACCA_BOOST_REFRESH_TRIGGER = new InjectionToken<AccaBoostRefreshTrigger>('ACCA_BOOST_REFRESH_TRIGGER');

export interface AccaBoostRefreshTrigger {
    getTrigger$(): Observable<void>;
}

@Injectable({ providedIn: 'root' })
export class AccaBoostTokenProviderService {
    private userService = inject(UserService);
    private accaBoostConfig = inject(AccaBoostConfig);
    private loaderService = inject(AccaBoostTokenLoaderService);
    private autoRefreshTrigger = inject(OptionalNonRootToken(ACCA_BOOST_REFRESH_TRIGGER), { optional: true });
    private productUtils = inject(SportsProductUtils);

    private readonly tokens = new ReplaySubject<AccaBoostToken[]>(1);
    private readonly manualRefresh$ = new Subject<void>();

    private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    readonly tokens$: Observable<AccaBoostToken[]> = this.tokens.asObservable();

    constructor() {
        if (this.accaBoostConfig.isEnabled) {
            this.startGettingToken();
        } else {
            this.tokens.next([]);
        }
    }

    refresh(): void {
        this.manualRefresh$.next();
    }

    private startGettingToken(): void {
        const canShowTokens$ = this.userService.onAuthenticationChange$.pipe(
            this.productUtils.filterSportsEmitLast(),
            map((event: UserEvent) => event instanceof UserLoginEvent || this.accaBoostConfig.allowUnAuthenticatedUsers),
            startWith(this.userService.isAuthenticated || this.accaBoostConfig.allowUnAuthenticatedUsers),
        );

        canShowTokens$
            .pipe(
                switchMap((canShowTokens) => {
                    const refreshTriggers: Observable<void>[] = [this.manualRefresh$];
                    const autRefreshTrigger$ = this.autoRefreshTrigger?.getTrigger$();
                    if (this.isBrowser && autRefreshTrigger$) {
                        refreshTriggers.push(autRefreshTrigger$);
                    }
                    const loadOnRefresh$ = merge(...refreshTriggers).pipe(switchMap(() => this.loadData()));

                    // when authenticated, load data and then subscribe to refreshing
                    return canShowTokens ? concat(this.loadData(), loadOnRefresh$) : of([]);
                }),
            )
            .subscribe((data: AccaBoostToken[]) => {
                this.tokens.next(data);
            });
    }

    private loadData(): Observable<AccaBoostToken[]> {
        return this.loaderService.loadTokens().pipe(map((data) => data.sort(this.byPriority)));
    }

    private byPriority = (first: AccaBoostToken, second: AccaBoostToken): number => first.priority - second.priority;
}
